/**
 * The transport. One place where a request is built, signed, sent and parsed.
 *
 * Three properties matter more than anything else in this file.
 *
 * 1. **The module never reads a token from storage.** It asks the host, every time, via
 *    `getToken`. The five hosts do not agree on where a session lives — ICRM keeps it in
 *    `localStorage`, Accounts and Assets use NextAuth's `getSession()` — and a module that
 *    picked one would be broken in three applications and, worse, would be reading a
 *    credential out of a store it does not own. `localStorage` appears nowhere in this
 *    package.
 * 2. **A mutating request without an idempotency key is refused here, in the caller's own
 *    process.** D-38: the transport must not invent one, because a fresh key per attempt
 *    turns a retry into a second posting. Failing locally is strictly better than sending
 *    it hopefully and getting a 400 back.
 * 3. **Every response is parsed through a schema.** A field the server renames arrives as
 *    a `ResponseShapeError` at the boundary rather than as `undefined` three components
 *    deep, and a `Numeric` string becomes an exact `Dec` before anything can multiply it.
 */
import { Dec } from '../numeric/decimal.js';
import { IdempotencyKeyRequiredError, NetworkError, ResponseShapeError, toApiError, UnauthorizedError, } from './errors.js';
import { IDEMPOTENCY_HEADER, REPLAY_HEADER } from './idempotency.js';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
function trimSlashes(value) {
    return value.replace(/\/+$/, '');
}
function encodeQuery(query) {
    if (!query)
        return '';
    const search = new URLSearchParams();
    for (const [key, raw] of Object.entries(query)) {
        const values = Array.isArray(raw) ? raw : [raw];
        for (const value of values) {
            if (value === undefined || value === null || value === '')
                continue;
            // A Dec is stringified exactly; a boolean must match Python's `true`/`false`.
            search.append(key, value instanceof Dec ? value.toString() : String(value));
        }
    }
    const text = search.toString();
    return text ? `?${text}` : '';
}
function zodIssues(error) {
    const issues = error.issues;
    if (!Array.isArray(issues))
        return [{ path: '', message: String(error) }];
    return issues.map((i) => ({
        path: Array.isArray(i.path) ? i.path.join('.') : '',
        message: i.message ?? 'invalid',
    }));
}
/**
 * Combine the caller's abort signal with a timeout.
 *
 * `AbortSignal.any` would do this in one line, but it is not available in every browser
 * the two Vite hosts still support, and a transport is the wrong place to discover that.
 */
function withTimeout(timeoutMs, external) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new Error(`timed out after ${timeoutMs}ms`)), timeoutMs);
    const relay = () => controller.abort(external?.reason);
    if (external) {
        if (external.aborted)
            relay();
        else
            external.addEventListener('abort', relay, { once: true });
    }
    return {
        signal: controller.signal,
        dispose: () => {
            clearTimeout(timer);
            external?.removeEventListener('abort', relay);
        },
    };
}
export class InventoryTransport {
    config;
    baseUrl;
    doFetch;
    constructor(config) {
        this.config = config;
        this.baseUrl = trimSlashes(config.baseUrl);
        const injected = config.fetch;
        this.doFetch =
            injected ??
                ((input, init) => {
                    const global = globalThis.fetch;
                    if (typeof global !== 'function') {
                        throw new Error('no fetch implementation is available; pass one as config.fetch when constructing the client');
                    }
                    return global(input, init);
                });
    }
    async send(spec) {
        const { method, path, query, body, operation, schema, signal } = spec;
        if (MUTATING.has(method) && !operation) {
            throw new IdempotencyKeyRequiredError(0, null, `${method} ${path} needs an idempotency key. Mint one per logical operation with ` +
                'beginOperation() and reuse it on every retry; the transport will not invent one, ' +
                'because a fresh key per attempt turns a retry into a duplicate posting.');
        }
        const headers = {
            Accept: 'application/json',
            ...this.config.headers,
        };
        if (body !== undefined)
            headers['Content-Type'] = 'application/json';
        if (operation)
            headers[IDEMPOTENCY_HEADER] = operation.key;
        const token = await this.config.getToken();
        if (token)
            headers.Authorization = `Bearer ${token}`;
        const url = `${this.baseUrl}${path}${encodeQuery(query)}`;
        const { signal: combined, dispose } = withTimeout(this.config.timeoutMs ?? 30_000, signal);
        let response;
        try {
            const init = { method, headers, signal: combined };
            if (body !== undefined)
                init.body = JSON.stringify(body);
            response = await this.doFetch(url, init);
        }
        catch (cause) {
            throw new NetworkError(path, cause);
        }
        finally {
            dispose();
        }
        const replayed = response.headers.get(REPLAY_HEADER) === 'true';
        if (replayed && operation && this.config.onReplay) {
            this.config.onReplay({ path, key: operation.key });
        }
        const text = await response.text();
        let payload = undefined;
        if (text.length > 0) {
            try {
                payload = JSON.parse(text);
            }
            catch {
                payload = text;
            }
        }
        if (!response.ok) {
            const detail = payload && typeof payload === 'object' && 'detail' in payload
                ? payload.detail
                : payload;
            const error = toApiError(response.status, detail, path, response.headers.get('x-request-id') ?? undefined);
            if (error instanceof UnauthorizedError && this.config.onUnauthorized) {
                await this.config.onUnauthorized(error);
            }
            throw error;
        }
        if (!schema) {
            return { data: undefined, status: response.status, replayed };
        }
        const parsed = schema.safeParse(payload);
        if (!parsed.success) {
            throw new ResponseShapeError(path, zodIssues(parsed.error), payload);
        }
        return { data: parsed.data, status: response.status, replayed };
    }
    /** The common case: the body, or a throw. */
    async request(spec) {
        const { data } = await this.send(spec);
        return data;
    }
}
//# sourceMappingURL=transport.js.map